import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import '../Admin/AdminPanel.css';

const AvailableProducts = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [beanTypes, setBeanTypes] = useState(['filter', 'espresso']);
  const [weights, setWeights] = useState(['250', '1000']);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'products'), (snapshot) => {
      const productsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setProducts(productsData);
    });
    return () => unsubscribe();
  }, []);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setLoading(true);
    setError(null);
    try {
      await deleteDoc(doc(db, 'products', productId));
    } catch (err) {
      setError('Error deleting product.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product.id);
    let prices = product.prices || {};
    let loadedBeanTypes = Object.keys(prices).length > 0 ? Object.keys(prices) : ['filter', 'espresso'];
    let loadedWeights = loadedBeanTypes.length > 0 ? Object.keys(prices[loadedBeanTypes[0]] || {}) : ['250', '1000'];
    setBeanTypes(loadedBeanTypes);
    setWeights(loadedWeights);
    loadedBeanTypes.forEach(type => {
      if (!prices[type]) prices[type] = {};
      loadedWeights.forEach(w => { if (!prices[type][w]) prices[type][w] = ''; });
    });
    setEditData({
      ...product,
      displayTitleField: product.displayTitleField || 'name',
      displayPriceField: product.displayPriceField || 'price',
      displayImageField: product.displayImageField || 'image',
      prices,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (productId) => {
    setLoading(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'products', productId), {
        ...editData,
        price: parseFloat(editData.price),
        stock: parseInt(editData.stock),
        displayTitleField: editData.displayTitleField,
        displayPriceField: editData.displayPriceField,
        displayImageField: editData.displayImageField,
        prices: editData.prices,
      });
      setEditingProduct(null);
    } catch (err) {
      setError('Error updating product.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditingProduct(null);
    setEditData({});
  };

  return (
    <div className="admin-section-page">
      <h2>Available Products</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <div className="products-list">
        {products.map((product) => (
          <div key={product.id} className="product-item">
            <img src={product.image} alt={product.name} />
            <div className="product-info">
              {editingProduct === product.id ? (
                <>
                  <input
                    type="text"
                    name="name"
                    value={editData.name}
                    onChange={handleEditChange}
                    placeholder="Product Name"
                  />
                  <label>
                    <input
                      type="radio"
                      name="displayTitleField"
                      value="name"
                      checked={editData.displayTitleField === 'name'}
                      onChange={handleEditChange}
                    />
                    Make it a Title
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={editData.price}
                    onChange={handleEditChange}
                    placeholder="Price"
                  />
                  <label>
                    <input
                      type="radio"
                      name="displayPriceField"
                      value="price"
                      checked={editData.displayPriceField === 'price'}
                      onChange={handleEditChange}
                    />
                    Make it a Price
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={editData.stock}
                    onChange={handleEditChange}
                    placeholder="Stock"
                  />
                  <input
                    type="text"
                    name="origin"
                    value={editData.origin}
                    onChange={handleEditChange}
                    placeholder="Origin"
                  />
                  <textarea
                    name="flavorNotes"
                    value={editData.flavorNotes}
                    onChange={handleEditChange}
                    placeholder="Flavor Notes"
                  />
                  <input
                    type="text"
                    name="variety"
                    value={editData.variety}
                    onChange={handleEditChange}
                    placeholder="Variety"
                  />
                  <input
                    type="text"
                    name="varietyMoreInfo"
                    value={editData.varietyMoreInfo || ''}
                    onChange={handleEditChange}
                    placeholder="Variety More Info (explain what this variety means)"
                  />
                  <input
                    type="text"
                    name="processingMethod"
                    value={editData.processingMethod}
                    onChange={handleEditChange}
                    placeholder="Processing Method"
                  />
                  <input
                    type="text"
                    name="processingMethodMoreInfo"
                    value={editData.processingMethodMoreInfo || ''}
                    onChange={handleEditChange}
                    placeholder="Processing Method More Info (explain what this method means)"
                  />
                  <input
                    type="text"
                    name="altitude"
                    value={editData.altitude}
                    onChange={handleEditChange}
                    placeholder="Altitude"
                  />
                  <input
                    type="text"
                    name="altitudeMoreInfo"
                    value={editData.altitudeMoreInfo || ''}
                    onChange={handleEditChange}
                    placeholder="Altitude More Info (explain what this altitude means)"
                  />
                  <select name="category" value={editData.category} onChange={handleEditChange}>
                    <option value="single-origin">Single Origin</option>
                    <option value="blend">Blend</option>
                    <option value="espresso">Espresso</option>
                    <option value="filter">Filter</option>
                    <option value="filter/espresso">Filter/Espresso</option>
                  </select>
                  <input
                    type="url"
                    name="image"
                    value={editData.image}
                    onChange={handleEditChange}
                    placeholder="Image URL"
                  />
                  <label>
                    <input
                      type="radio"
                      name="displayImageField"
                      value="image"
                      checked={editData.displayImageField === 'image'}
                      onChange={handleEditChange}
                    />
                    Make it an Image for the menu card
                  </label>
                  <textarea
                    name="description"
                    value={editData.description}
                    onChange={handleEditChange}
                    placeholder="Description"
                  />
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Price Matrix</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input type="text" placeholder="Add bean type" id="add-bean-type" style={{ width: 120 }} />
                      <button onClick={() => {
                        const val = document.getElementById('add-bean-type').value.trim();
                        if (val && !beanTypes.includes(val)) {
                          setBeanTypes([...beanTypes, val]);
                          setEditData(prev => ({
                            ...prev,
                            prices: { ...prev.prices, [val]: Object.fromEntries(weights.map(w => [w, ''])) }
                          }));
                          document.getElementById('add-bean-type').value = '';
                        }
                      }}>Add Bean Type</button>
                      <input type="text" placeholder="Add weight (g)" id="add-weight" style={{ width: 120 }} />
                      <button onClick={() => {
                        const val = document.getElementById('add-weight').value.trim();
                        if (val && !weights.includes(val)) {
                          setWeights([...weights, val]);
                          setEditData(prev => ({
                            ...prev,
                            prices: Object.fromEntries(beanTypes.map(type => [type, { ...prev.prices[type], [val]: '' }]))
                          }));
                          document.getElementById('add-weight').value = '';
                        }
                      }}>Add Weight</button>
                    </div>
                    <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
                      <thead>
                        <tr>
                          <th style={{ border: '1px solid #ccc', padding: 4 }}></th>
                          {weights.map(w => (
                            <th key={w} style={{ border: '1px solid #ccc', padding: 4, position: 'relative' }}>
                              {w}g
                              <button onClick={() => {
                                setWeights(weights.filter(ww => ww !== w));
                                setEditData(prev => ({
                                  ...prev,
                                  prices: Object.fromEntries(beanTypes.map(type => [type, Object.fromEntries(weights.filter(ww => ww !== w).map(ww => [ww, prev.prices[type]?.[ww] || '']))]))
                                }));
                              }} style={{ position: 'absolute', right: 2, top: 2, fontSize: 12, color: '#e00', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {beanTypes.map(type => (
                          <tr key={type}>
                            <td style={{ border: '1px solid #ccc', padding: 4, fontWeight: 500, position: 'relative' }}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                              <button onClick={() => {
                                setBeanTypes(beanTypes.filter(t => t !== type));
                                setEditData(prev => ({
                                  ...prev,
                                  prices: Object.fromEntries(beanTypes.filter(t => t !== type).map(t => [t, prev.prices[t] || {}]))
                                }));
                              }} style={{ position: 'absolute', right: 2, top: 2, fontSize: 12, color: '#e00', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                            </td>
                            {weights.map(w => (
                              <td key={w} style={{ border: '1px solid #ccc', padding: 4 }}>
                                <input
                                  type="number"
                                  value={editData.prices?.[type]?.[w] || ''}
                                  onChange={e => setEditData(prev => ({
                                    ...prev,
                                    prices: {
                                      ...prev.prices,
                                      [type]: {
                                        ...prev.prices[type],
                                        [w]: e.target.value
                                      }
                                    }
                                  }))}
                                  placeholder={`Price for ${w}g`}
                                  style={{ width: 80 }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={() => handleEditSave(product.id)} disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleEditCancel} disabled={loading}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <h4>{product.name}</h4>
                  <p>{product.price?.toLocaleString()} QAR</p>
                  <p>Stock: {product.stock || 'N/A'}</p>
                  <p>Category: {product.category}</p>
                  {product.variety && (
                    <p>
                      <strong>Variety:</strong> {product.variety}
                    </p>
                  )}
                  {product.varietyMoreInfo && (
                    <p style={{ fontSize: '0.95em', color: '#888' }}>
                      <em>Variety Info:</em> {product.varietyMoreInfo}
                    </p>
                  )}
                  {product.processingMethod && (
                    <p>
                      <strong>Processing Method:</strong> {product.processingMethod}
                    </p>
                  )}
                  {product.processingMethodMoreInfo && (
                    <p style={{ fontSize: '0.95em', color: '#888' }}>
                      <em>Processing Method Info:</em> {product.processingMethodMoreInfo}
                    </p>
                  )}
                  {product.altitude && (
                    <p>
                      <strong>Altitude:</strong> {product.altitude}
                    </p>
                  )}
                  {product.altitudeMoreInfo && (
                    <p style={{ fontSize: '0.95em', color: '#888' }}>
                      <em>Altitude Info:</em> {product.altitudeMoreInfo}
                    </p>
                  )}
                  <button onClick={() => handleEditClick(product)} disabled={loading}>
                    Edit
                  </button>
                  <button onClick={() => handleDeleteProduct(product.id)} disabled={loading}>
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailableProducts;
